// crypto.getRandomValues and Uint8Array work in both Node.js (>=15) and all modern browsers,
// avoiding the need for a require("crypto") call that breaks in Vite's ESM browser context.
const rando = (buf: Uint8Array): Uint8Array => crypto.getRandomValues(buf);
const alloc = (size: number): Uint8Array => new Uint8Array(size);

let pool: Uint8Array;
let poolOffset: number;

/**
 * It is best to make fewer, larger requests to the crypto module to
 * avoid system call overhead. So, random numbers are generated in a
 * pool. The pool is a Buffer that is larger than the initial random
 * request size by a factor of 32. The pool is enlarged if subsequent
 * requests exceed the maximum buffer size.
 */
function random(size: number): Uint8Array {
  if (!pool || pool.length < size) {
    pool = alloc(size * 32);
    rando(pool);
    poolOffset = 0;
  } else if (poolOffset + size > pool.length) {
    rando(pool);
    poolOffset = 0;
  }

  const res = pool.subarray(poolOffset, poolOffset + size);
  poolOffset += size;
  return res;
}

/**
 * This alphabet is drawn from github.com/ai/nanoid and is a reordering of alphanumeric characters
 * that is optimized for better gzip compression
 */
const urlAlphabet = "ModuleSymbhasOwnPro0123456789ABCDEFGHNRVfgctiUvzWKqYTJkLxpZXIjQW";

/**
 * uid produces a random string inspired by github.com/ai/nanoid.
 *
 * In node.js, it utilizes the node.js crypto module and it uses the browser crypto
 * module in browsers. It is made significantly more efficient by making more coarse, pooled requests
 * to these modules.
 */
export default function uid(size: number = 16): string {
  const bytes = random(size);
  let id = "";
  while (size--) {
    id += urlAlphabet[bytes[size] & 63];
  }
  return id;
}

export function isUid(id: string): boolean {
  return typeof id === "string" && id.length === 16 && /^[a-zA-Z0-9]+$/.test(id);
}

export function containsUid(str: string): boolean {
  if (typeof str !== "string") {
    return false;
  }

  // Look for any 16-character alphanumeric substring
  const uidPattern = /[a-zA-Z0-9]{16}/;
  return uidPattern.test(str);
}
